import {useState, useEffect, useMemo} from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { OverlayTrigger } from 'react-bootstrap';
import styled from 'styled-components';
import BllinkUtils from '../../../common/utils/utils';
import { constants } from '../../../common/constants/constants';
import { renderIcon, renderOneTimeIcon, renderTotalIcon } from '../../../common/utils/yearlyRowsLogic';
import { isAdminSelector } from '../../../store/auth/selectors';
import { StyledExpensesTooltip } from '../expensesMonthlyNew2/MonthlyExpensesNew.styled';
import { StyledCardWrapper, StyledYearlyCard } from "./YearlyByMonths.styled";
import {ActionsButton} from "../../../common/actions_button/ActionsButton";

const StringUtils = BllinkUtils.Strings;
const FilterUtils = BllinkUtils.Filter;

const StyledNameTooltip = styled(StyledExpensesTooltip)`
    .tooltip-inner {
        color: #373647;
    }
`

const YearlyCard = ({ record, buildingID, oneTimeHeaders, filter, csvCallback, hideTooltip, renderCSV, year, selectedYear, aptNotesClickCallback, aptStopPlansCallback, managerUpdateCallback, editApartmentPaymentsCallback }) => {

    const { apartmentNumber, tenantName, total } = record;
    const { t } = useTranslation();
    const isAdmin = useSelector(isAdminSelector);

    const [oneTimeSum, setOneTimeSum] = useState();

    const actions = useMemo(() => {
        const aa = [
            { action: 'chargeApt', label: t('reports.chargeApt') },
            { action: 'aptNotes', label: t('managerAutoCampaign.activeCampaign.btns.aptNotes') },
            { action: 'changeChargeDate', label: t('reports.edit_apartment_payments.action') },
            // { action: 'stopPlan', label: t('reports.stop_plans.action') },
        ];
        if (isAdmin) {
            aa.push({ action: 'managerUpdate', label: t('reports.yearlyByMonths.managerUpdateApartment') });
        }
        return aa;
    }, [isAdmin]);

    const redirectToConfirmationPage = (apartmentNumber) => window.open(`/manager-fill-details/${buildingID}?apt=${apartmentNumber}`, '_blank');

    const totalToPay = StringUtils.numberWithCommas(total?.debt.value + total?.future.value);

    const monthNames = Object.values(constants.MonthsToText);
    const paidMonths = Object.entries(record).filter(([key]) => monthNames.includes(key));
    const paidMonthsValues = paidMonths.map(([month, value]) => value?.paid?.value);
    const paidMonthsObjects = paidMonths.reduce((acc, [month, value]) => {
        acc[month] = value?.paid?.value;
        return acc;
    }, {});
    let filterTenantName = [];
    let csvTenantName = {};
    if (Array.isArray(tenantName) && tenantName.length >= 1) {
        tenantName.forEach(tenant => {
            filterTenantName.push(tenant.tenantName);
            if (tenant.tenantType !== 'unknown') csvTenantName[tenant.tenantType] = tenant.tenantName
        });
    }

    const showItem = FilterUtils.filterItems([apartmentNumber, ...filterTenantName, totalToPay, ...paidMonthsValues], filter);

    let oneTimeSumObj = {
        amount_left: {value: 0, currency: ''},
        paid: {value: 0, currency: ''},
        scheduled_to_pay: {value: 0, currency: ''},
        total: {value: 0, currency: ''}
    };

    const sumOneTime = arr => {
        if (arr.length === 0) return setOneTimeSum(null)
        else if (arr.length === 1) setOneTimeSum(arr[0][1])
        else if(arr.length > 1) {
            arr.map(el => {
                const current = el[1];
                oneTimeSumObj.amount_left.value += +current.amount_left.value;
                oneTimeSumObj.total.value += +current.total.value;
                oneTimeSumObj.paid.value += +current.paid.value;
                oneTimeSumObj.scheduled_to_pay.value += +current.scheduled_to_pay.value;
                oneTimeSumObj.amount_left.currency = current.amount_left.currency;
                oneTimeSumObj.total.currency = current.total.currency;
                oneTimeSumObj.paid.currency = current.paid.currency;
                oneTimeSumObj.scheduled_to_pay.currency = current.scheduled_to_pay.currency;
                return oneTimeSumObj
            })
            setOneTimeSum(oneTimeSumObj);
        }
    };

    useEffect(() => {
        const oneTimeArrays = Object.entries(record).filter(([key]) => key.includes('one_time'));
        sumOneTime(oneTimeArrays);
    }, [record]);

    useEffect(() => renderCSV && (oneTimeSum || oneTimeSum === null) && csvCallback({
        apartmentNumber,
        ...paidMonthsObjects,
        oneTime: oneTimeSum?.paid.value,
        totalCharged: total?.charged.value,
        totalDebt: total?.debt.value,
        totalFuture: total?.future.value
    }), [oneTimeSum, record]);

    const renderTenantName = () => {
        if (tenantName.length > 1) {
            return (
                <>
                    <div>
                        <b>{tenantName[0].tenantName}</b> {`(${t(`payments.${tenantName[0].tenantType}`)})`}
                    </div>
                    <div>
                        <b>{tenantName[1].tenantName}</b> {`(${t(`payments.${tenantName[1].tenantType}`)})`}
                    </div>
                </>

            )
        } else if (tenantName.length === 1 && tenantName[0].tenantType !== 'unknown') {
            return <div><b>{tenantName[0].tenantName}</b> {`(${t(`payments.${tenantName[0].tenantType}`)})`}</div>
        } else if (tenantName.length === 1 && tenantName[0].tenantType !== 'renter' || tenantName[0].tenantType !== 'owner') {
            return <div><b>{tenantName[0].tenantName}</b></div>
        }
    };

    const onAptNumClick = month => window.open(`/reports/apartment/${buildingID}/${apartmentNumber}`);

    function handleAction(action) {
        switch (action) {
            case 'chargeApt': redirectToConfirmationPage(apartmentNumber); break;
            case 'aptNotes': aptNotesClickCallback(buildingID, apartmentNumber); break;
            case 'changeChargeDate': editApartmentPaymentsCallback({ record, buildingID, year }); break;
            case 'stopPlan': aptStopPlansCallback({ record, buildingID, year }); break;
            case 'managerUpdate': managerUpdateCallback({ record, buildingID, year, selectedYear }); break;
            default: throw Error(`Unexpected action: ${action}`);
        }
    }

    return (
        showItem && <StyledCardWrapper hideTooltip={hideTooltip}>

            <StyledYearlyCard>
                <OverlayTrigger placement="bottom" overlay={props => (<StyledNameTooltip {...props}>{renderTenantName()}</StyledNameTooltip>)}>
                    <span className='apartment' onClick={onAptNumClick}>{apartmentNumber}</span>
                </OverlayTrigger>
                {monthNames.map((month, i) => renderIcon(record[month], {}, isAdmin, i))}
                <span className='one-time'>{oneTimeHeaders.length > 0 && renderOneTimeIcon(oneTimeSum, isAdmin)}</span>
                {renderTotalIcon(total)}
            </StyledYearlyCard>

            { !hideTooltip && (
                <ActionsButton
                    actions={actions}
                    onAction={handleAction}
                />
            ) }

        </StyledCardWrapper>
    );
};

export default YearlyCard;
